import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useFetch, FetchError } from '@/modules/core/composables/useFetch'
import { useFetch as useFetchV3 } from '@/modules/core/stores/fetch'
import { stringifyKey, type Key } from '@/modules/core/stores/kv'
import { initialMoney, sumMoney, type Money, toNumber } from '@/modules/shared/utils/money'
import { roi } from '@/modules/shared/utils/reporting'
import { parseJSON, format } from 'date-fns'
import { CloseReadiness, ExitReadiness, Invoice } from '../types'

///////////////////////////////////////////////////////////////////////////////

export enum InvestorType {
  Individual = 'individual',
  Fund = 'fund',
  GP = 'gp',
  SPV = 'spv',
}

export enum EntityType {
  Fund = 'fund',
  GP = 'gp',
  SPV = 'spv',
}

///////////////////////////////////////////////////////////////////////////////

export interface DealCard {
  id: string
  name: string
  description: string
  website: string
  sectors: string[]
  investment_stage: string
  investment_type: string
  other_investment_type: string
  fund_type: string
  currency: string
  expected_close_date: string
  visibility: 'all_investors' | 'selected'
  company_id: string
  filing_folder_id: string
  target_raise: Money
  pre_money_valuation: Money
  minimum_commitment: Money
  invited_investor_ids: string[]
}

export interface Entity {
  id: string
  date: string
  type: EntityType
  address: Address
  admin_signer_id: string
  basis: string
  capital_call_frequency: string
  capital_call_wiring_instructions: string
  computed_wiring_instructions: string | null
  carried_interest_percentage: number
  carried_interest: number
  cid: string
  currency: string
  date: string
  date_established: string
  date_exited: string
  deal_card: DealCard | null
  entity_type: 'spv' | 'gp' | 'fund'
  filing_folder_root_id: string
  compliance_filing_folder_id: string
  fund_gp_name: string
  gosystem_locator: string
  has_interest_catch_up_provision: boolean
  insights: any
  initial_closing_date: string
  investment_period_end_date: string
  is_filing_required: boolean
  is_billing_paid: boolean
  is_billing_settled: boolean
  has_billing_invoices: boolean
  is_legacy_billing: boolean
  is_venture360_the_administrator: boolean
  is_venture360_forming_this_fund: boolean
  is_venture360_providing_formation_documents: boolean
  investor_set_profile: {
    is_requesting_bank_connection: boolean
    is_qsbs_qualified: boolean
    is_safe_spv_structured: boolean
  }
  expect_any_activity: boolean
  kind_of_activity: string[]
  disbursement_method: 'check' | 'wire' | 'other'
  disbursement_wire_bank_name: string
  disbursement_wire_bank_address: string
  disbursement_wire_bank_routing_number: string
  disbursement_wire_bank_account_number: string
  disbursement_wire_repeat_bank_account_number: string
  disbursement_wire_bank_swift_code: string
  disbursement_wire_account_name: string
  disbursement_wire_for_further_credit_to: string
  disbursement_wire_bank_notes: string
  legal_name: string
  management_fee_percentage: number
  manager: { cid: string; id: string; name: string } | null
  manager_id: string
  manager_type: string
  name: string
  not_filing_reason: string
  ownership_type: 'committed' | 'capital_called' | 'assigned'
  preferred_return_percentage: number
  selected_billing_frequency: string
  selected_services: string[]
  shareholders: Investor[]
  state_of_incorporation: string
  state_filing: string[]
  structure: any
  tasking_task_list_id: number
  tax_id: string
  starting_tax_year: number
  ending_tax_year: number
  waterfall_calculation: string
  lifecycle_state: 'raising' | 'closed' | 'exited'
  investor_range: string
  is_raising_under_250k: boolean
  deal_card: DealCard | null
}

export interface Admin {
  id: string
  individual_id: string
  name: string
}

export interface Address {
  city: string
  country: string
  county: string
  line1: string
  line2: string
  phone: string
  postal_code: string
  state: string
}

export interface Call {
  id: string
  batch_id: string
  call_date: string
  commitment_id: string
  investor_id: string
  investor_type: InvestorType
  is_investor_transfer: boolean
  shareholder_id: string
  shareholder_type: InvestorType
  invoice_id: string
  due_date: string
  called: Money
  capital: Money
  invoice: Invoice
  is_outstanding: boolean
  is_settled: boolean
  management_fee: Money
  other_fee: Money
  status: string
  type: string
}

export interface CallExpanded extends Call {
  commitment: Commitment
  investor: Investor
  shareholder: Investor
  investor_set: Entity
  invoice: Invoice
}

export interface Commitment {
  id: string
  investor_id: string
  investor_type: InvestorType
  shareholder_id: string
  shareholder_type: InvestorType
  called: Money
  capital_called: Money
  carried_interest_percentage: number
  cash_position: Money
  committed: Money
  commitment_remaining: Money
  date: string
  fees_called: Money
  hurdle_remaining: Money
  management_fee_called: Money
  management_fee_percentage: number
  net_distributed: Money
  other_fee_called: Money
  ownership: number
  ownership_by_commitment: number
  ownership_computed: number
  ownership_override: number
  preferred_return_percentage: number
  shareholder: Investor
  called_as_of?: Money
}

export interface CommitmentExpanded extends Commitment {}

export interface Company {
  id: string
  industry_id: string
  initials: string
  logo_url: string
  name: string
}

export interface CompanyExpanded extends Company {
  accrued_interest: Money
  current_price: Money
  current_shares: number
  current_value: Money
  distributed: Money
  distributed_received: Money
  initial_price: Money
  initial_shares: number
  initial_value: Money
  roi: number
}

export interface Distribution {
  id: string
  investor_id: string
  investor_type: InvestorType
  investor_name: string
  batch_id: string
  carried_interest: Money
  company_id: string
  date: string
  gross: Money // aka distributed
  interest: Money
  is_recallable: boolean
  net: Money
  other_fee: Money
  payment_method: string
  payment_status: string
  payment_status_marked_at: string | null
  preferred_return: Money
  profit: Money
  return_of_capital: Money
  shareholder_id: string
  shareholder_type: InvestorType
  type: string
}

export interface DistributionExpanded extends Distribution {
  company: Company
  commitment: Commitment
  investor: Investor
}

export interface Industry {
  id: string
  name: string
}

export interface IndustryExpanded extends Industry {
  current_value: Money
  initial_value: Money
}

export interface Insights {
  initial_value: Money
  current_value: Money
}

// Response shape of GET /v4/:slug/investing/investor-sets/:id/insights.
// Returns computed values only: performance metrics, plus the commitment-
// filtered variants of structure and document_status (when shareholder_cids
// is passed). Structural lists like shareholders stay on #show.
export interface MetricSeriesPoint {
  date: string
  value_amount: number
}

export interface MetricSeriesResponse {
  currency: string
  start_year: number | null
  points: MetricSeriesPoint[]
}

export interface EntityInsights {
  id: string
  insights: any
  structure: any
  document_status: string | null
}

export interface Investment {
  id: string
  investor_id: string
  investor_type: InvestorType
  legacy_investor_type: string // as the name implies, this is a legacy field
  accrued_interest: Money
  company_id: string
  current_price: Money
  current_shares: number
  current_value: Money
  date: string
  distributed: Money
  distributed_received: Money
  industry_id: string
  initial_price: Money
  initial_shares: number
  initial_value: Money
  investment_type: string
  method: string
  method_label_ancestry: string
  shareholder_id: string
  shareholder_type: InvestorType
  term_sheet_id: string
}

export interface Investor {
  type: InvestorType
  id: string
  cid: string
  address: Address
  email: string
  name: string
  investing_name: string
  tax_id: string
  callIds: Key[]
  commitmentIds: Key[]

  disbursement_method: string
  disbursement_wire_bank_name: string
  disbursement_wire_bank_address: string
  disbursement_wire_bank_routing_number: string
  disbursement_wire_bank_account_number: string
  disbursement_wire_bank_swift_code: string
  disbursement_wire_account_name: string
  disbursement_wire_for_further_credit_to: string
  disbursement_check_receiver_name: string
  disbursement_other_details: string
  disbursement_method_confirmed_at: string
}

export interface Note {
  id: string
  entity_id: string
  entity_type: EntityType
  text: string
}

export interface TermSheet {
  id: string
  name: string
  terms: any
  investment_type: string
}

export interface TermSheetExpanded extends TermSheet {
  accrued_interest: Money
  company: Company
  current_price: Money
  current_shares: number
  current_value: Money
  distributed: Money
  industry: Industry
  initial_price: Money
  initial_shares: number
  initial_value: Money
}

export interface EntityExpanded extends Entity {
  admins: Admin[]
  admin_signer: Admin | null
  calls: Call[]
  commitments: Commitment[]
  companies: Company[]
  distributions: Distribution[]
  industries: Industry[]
  investments: Investment[]
  investors: Investor[]
  invoices: Invoice[]
  manager: Investor | null
  notes: Note[]
  shareholders: Investor[]
  term_sheets: TermSheet[]
}

export const useEntityStore = defineStore('entityStore', () => {
  const entity = ref<Entity>(null)
  const entityExpanded = ref<EntityExpanded>(null)
  const metricSeries = ref<MetricSeriesResponse | null>(null)
  const metricSeriesLoading = ref(false)

  /////////////////////////////////////////////////////////////////////////////
  // Actions
  /////////////////////////////////////////////////////////////////////////////

  const fetch = async (id: string, { slug }: { slug: string }) => {
    const data = await useFetchV3(`/${slug}/investing/b/entities/${id}`).get<EntityExpanded>()
    entity.value = data
  }

  const fetchEntity = async (
    id: string,
    { slug, shareholder_cids, dateRange }: { slug: string; shareholder_cids?: string[]; dateRange?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    if (dateRange) params.set('date_range', dateRange.join(','))
    const queryParams = params.toString() ? `?${params}` : ''

    const data = await useFetch(`/v4/${slug}/investing/investor-sets/${id}${queryParams}`).get<EntityExpanded>()
    entity.value = { ...data }
    entityExpanded.value = { ...data }
  }

  // Fetches the entity's performance metrics from GET
  // /investor-sets/:id/insights and merges into the entity state. Call after
  // fetchEntity has populated the store — see venture360/tickets#1837.
  const fetchInsights = async (
    id: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    const queryParams = params.toString() ? `?${params}` : ''

    let data: EntityInsights
    try {
      data = await useFetch(`/v4/${slug}/investing/investor-sets/${id}/insights${queryParams}`).get<EntityInsights>()
    } catch (err) {
      if (err instanceof FetchError && (err.status === 403 || err.status === 404)) return
      throw err
    }

    const merge = {
      insights: data.insights,
      structure: data.structure,
      document_status: data.document_status,
    }
    if (entity.value) entity.value = { ...entity.value, ...merge }
    if (entityExpanded.value) entityExpanded.value = { ...entityExpanded.value, ...merge }
  }

  const fetchCalls = async (
    entityId: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    await fetchEntity(entityId, { slug, shareholder_cids })

    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    const queryParams = params.toString() ? `?${params}` : ''

    const calls = await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/calls${queryParams}`).get<
      CallExpanded[]
    >()
    entityExpanded.value.calls = calls
  }

  const fetchDistributions = async (
    entityId: string,
    { slug, shareholder_cids }: { slug: string; shareholder_cids?: string[] },
  ) => {
    await fetchEntity(entityId, { slug, shareholder_cids })

    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    const queryParams = params.toString() ? `?${params}` : ''

    const distributions = await useFetch(
      `/v4/${slug}/investing/investor-sets/${entityId}/distributions${queryParams}`,
    ).get<DistributionExpanded[]>()
    entityExpanded.value.distributions = distributions
  }

  const fetchCommitments = async (
    entityId: string,
    { slug, asOf, called_as_of }: { slug: string; asOf?: string; called_as_of?: string },
  ) => {
    await fetchEntity(entityId, { slug })

    const params = new URLSearchParams()
    if (asOf) params.set('as_of', asOf)
    if (called_as_of) params.set('called_as_of', called_as_of)
    const queryParams = params.toString() ? `?${params}` : ''

    const commitments = await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/commitments${queryParams}`).get<
      CommitmentExpanded[]
    >()
    entityExpanded.value.commitments = commitments
  }

  const fetchInvestments = async (
    entityId: string,
    { slug, shareholder_cids, date }: { slug: string; shareholder_cids?: string[]; date?: string },
  ) => {
    await fetchEntity(entityId, { slug, shareholder_cids })

    const params = new URLSearchParams()
    if (shareholder_cids) params.set('shareholder_cids', shareholder_cids.join(','))
    if (date) params.set('date', date)

    const queryParams = params.toString() ? `?${params}` : ''

    const investments = await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/investments${queryParams}`).get<
      Investment[]
    >()
    entityExpanded.value.investments = investments
  }

  const fetchMetricSeries = async (entityId: string, { slug, year }: { slug: string; year?: number | null }) => {
    metricSeriesLoading.value = true
    try {
      const query = year ? `?year=${year}` : ''
      metricSeries.value = await useFetch(
        `/v4/${slug}/investing/investor-sets/${entityId}/metric-series${query}`,
      ).get<MetricSeriesResponse>()
    } catch {
      metricSeries.value = null
    } finally {
      metricSeriesLoading.value = false
    }
  }

  //////////////////////////////////////////////////////////////////////////////
  // Commands
  //////////////////////////////////////////////////////////////////////////////

  const generateOnboardingTaskList = async (entityId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/generate-onboarding-task-list`).post()
  }

  const documentSigningTask = async (entityId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/signing/documents/document-signing-task/${entityId}`).post()
  }

  const setupSpv = async (spv: any, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/investor-sets/setup-spv`).post({ spv })
    return data
  }

  const updateTaxId = async (entityId: string, taxId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/tax_id`).post({
      investor_set: { tax_id: taxId },
    })
  }

  const setupFund = async (fund: any, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/investor-sets/setup-fund`).post({ fund })
    return data
  }

  const deleteEntity = async (entityId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/remove`).delete()
    reset()
  }

  const closeEntity = async (
    entityId: string,
    { slug, closing_documents }: { slug: string; closing_documents?: File[] },
  ) => {
    if (closing_documents && closing_documents.length > 0) {
      const formData = new FormData()
      for (const file of closing_documents) {
        formData.append('closing_documents[]', file)
      }
      await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/close`).post(formData)
    } else {
      await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/close`).post()
    }
    await fetchEntity(entityId, { slug })
    await fetchInsights(entityId, { slug })
  }

  const exitEntity = async (
    entityId: string,
    {
      slug,
      exit_date,
      exit_type,
      exit_documents,
    }: { slug: string; exit_date?: string; exit_type?: string; exit_documents?: File[] },
  ) => {
    const formData = new FormData()
    if (exit_date) formData.append('exit_date', exit_date)
    if (exit_type) formData.append('exit_type', exit_type)
    if (exit_documents) {
      for (const file of exit_documents) {
        formData.append('exit_documents[]', file)
      }
    }
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/exit`).post(formData)
    await fetchEntity(entityId, { slug })
    await fetchInsights(entityId, { slug })
  }

  const fetchExitReadiness = async (entityId: string, { slug }: { slug: string }): Promise<ExitReadiness> => {
    return await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/exit-readiness`).get<ExitReadiness>()
  }

  const fetchCloseReadiness = async (entityId: string, { slug }: { slug: string }): Promise<CloseReadiness> => {
    return await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/close-readiness`).get<CloseReadiness>()
  }

  const reopenEntity = async (entityId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/reopen`).post()
    await fetchEntity(entityId, { slug })
    await fetchInsights(entityId, { slug })
  }

  const recloseEntity = async (entityId: string, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/investor-sets/${entityId}/reclose`).post()
    await fetchEntity(entityId, { slug })
    await fetchInsights(entityId, { slug })
  }

  const reset = () => {
    entity.value = null
    entityExpanded.value = null
    metricSeries.value = null
    metricSeriesLoading.value = false
  }
  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////

  const createGet = <T>(key: string) => {
    return (id: string | number): T => {
      return entityExpanded.value?.[key]?.find((item: T) => item.id === id)
    }
  }
  const createList = <T>(key: string): T[] => entityExpanded.value?.[key] ?? []
  const createComputedList = <T>(key: string) => computed<T[]>(() => createList<T>(key))
  const createListBy = <T>(property: string) => {
    return (investorKeys: string[]): T[] => {
      const investorKeySet = new Set(investorKeys)

      return (
        entityExpanded.value?.[property]?.filter((record: { investor_id: string; investor_type: string }) => {
          const currentKey = stringifyKey([record.investor_id, record.investor_type])
          return investorKeySet.has(currentKey)
        }) ?? []
      )
    }
  }

  // Selected investors

  const selectedInvestorKeys = ref<string[]>([])

  watch(entity, (value) => {
    if (value) {
      // selectAllInvestorKeys()
    }
  })

  const createKeyForInvestor = (investor: Investor) => {
    return stringifyKey([investor.id, investor.type])
  }

  const selectAllInvestorKeys = () => {
    selectedInvestorKeys.value = entity.value?.shareholders.map((i) => createKeyForInvestor(i)) ?? []
  }

  ///////////////////////////////////////////////////////////////////////////////
  // Admins
  ///////////////////////////////////////////////////////////////////////////////

  const getAdmin = createGet<Admin>('admins')
  const listAdmins = createComputedList<Admin>('admins')

  ///////////////////////////////////////////////////////////////////////////////
  // Calls
  ///////////////////////////////////////////////////////////////////////////////

  const getCall = createGet<Call>('calls')
  const listCalls = createComputedList<Call>('calls')
  const listCallsForInvestors = createListBy<Call>('calls')
  const listCallsForSelectedInvestors = computed(() => listCallsForInvestors(selectedInvestorKeys.value))

  ///////////////////////////////////////////////////////////////////////////////
  // Commitments
  ///////////////////////////////////////////////////////////////////////////////

  const getCommitment = createGet<Commitment>('commitments')
  const listCommitments = createComputedList<Commitment>('commitments')
  const listCommitmentsForInvestors = createListBy<Commitment>('commitments')
  const listCommitmentsForSelectedInvestors = computed(() => listCommitmentsForInvestors(selectedInvestorKeys.value))

  const getCommitmentTotalsForSelectedInvestors = computed(() => {
    const money = {
      ...initialMoney,
      currency: entity.value?.currency || 'USD',
    }

    return listCommitmentsForSelectedInvestors.value.reduce(
      (acc, commitment) => {
        acc.cash_position = sumMoney([acc.cash_position, commitment.cash_position])
        acc.committed = sumMoney([acc.committed, commitment.committed])
        acc.commitment_remaining = sumMoney([acc.commitment_remaining, commitment.commitment_remaining])
        acc.hurdle_remaining = sumMoney([acc.hurdle_remaining, commitment.hurdle_remaining])
        return acc
      },
      {
        cash_position: { ...money },
        committed: { ...money },
        commitment_remaining: { ...money },
        hurdle_remaining: { ...money },
      },
    )
  })

  ///////////////////////////////////////////////////////////////////////////////
  // Companies
  ///////////////////////////////////////////////////////////////////////////////

  const getCompany = createGet<Company>('companies')
  const listCompanies = createComputedList<Company>('companies')

  ///////////////////////////////////////////////////////////////////////////////
  // Distributions
  ///////////////////////////////////////////////////////////////////////////////

  const getDistribution = createGet<Distribution>('distributions')
  const listDistributions = createComputedList<Distribution>('distributions')
  const listDistributionsForInvestors = createListBy<Distribution>('distributions')
  const listDistributionsForSelectedInvestors = computed(() =>
    listDistributionsForInvestors(selectedInvestorKeys.value),
  )

  const getDistributionTotalsForSelectedInvestors = computed(() => {
    const money = {
      ...initialMoney,
      currency: entity.value?.currency || 'USD',
    }

    return listDistributionsForSelectedInvestors.value.reduce(
      (acc, distribution) => {
        acc.carried_interest = sumMoney([acc.carried_interest, distribution.carried_interest])
        acc.interest = sumMoney([acc.interest, distribution.interest])
        acc.net = sumMoney([acc.net, distribution.net])
        acc.other_fee = sumMoney([acc.other_fee, distribution.other_fee])
        acc.preferred_return = sumMoney([acc.preferred_return, distribution.preferred_return])
        acc.profit = sumMoney([acc.profit, distribution.profit])
        acc.return_of_capital = sumMoney([acc.return_of_capital, distribution.return_of_capital])

        return acc
      },
      {
        carried_interest: { ...money },
        interest: { ...money },
        net: { ...money },
        other_fee: { ...money },
        preferred_return: { ...money },
        profit: { ...money },
        return_of_capital: { ...money },
      },
    )
  })

  ///////////////////////////////////////////////////////////////////////////////
  // Industries
  ///////////////////////////////////////////////////////////////////////////////

  const getIndustry = createGet<Industry>('industries')
  const listIndustries = createComputedList<Industry>('industries')

  ///////////////////////////////////////////////////////////////////////////////
  // Investments
  ///////////////////////////////////////////////////////////////////////////////

  const getInvestment = createGet<Investment>('investments')
  const listInvestments = createComputedList<Investment>('investments')
  const listInvestmentsForInvestors = createListBy<Investment>('investments')
  const listInvestmentsForSelectedInvestors = computed(() => listInvestmentsForInvestors(selectedInvestorKeys.value))
  const listExpandedInvestmentsForSelectedInvestors = computed(() =>
    listInvestmentsForSelectedInvestors.value.map((investment) => {
      return {
        ...investment,
        company: getCompany(investment.company_id),
        industry: getIndustry(investment.industry_id),
        investor: getInvestor(investment.investor_id, investment.investor_type),
        roi: roi({
          current_value: toNumber(investment.current_value),
          distributed: toNumber(investment.distributed),
          initial_value: toNumber(investment.initial_value),
        }),
        term_sheet: getTermSheet(investment.term_sheet_id),
      }
    }),
  )

  const getInvestmentTotalsForSelectedInvestors = computed(() => {
    const money = {
      ...initialMoney,
      currency: entity.value?.currency || 'USD',
    }

    return listInvestmentsForSelectedInvestors.value.reduce(
      (acc, investment) => {
        acc.currentValue = sumMoney([acc.currentValue, investment.current_value])
        acc.initialValue = sumMoney([acc.initialValue, investment.initial_value])
        return acc
      },
      {
        currentValue: { ...money },
        initialValue: { ...money },
      },
    )
  })

  ///////////////////////////////////////////////////////////////////////////////
  // Investors
  ///////////////////////////////////////////////////////////////////////////////

  const getShareholder = (id: string, type: InvestorType): Investor => {
    return listShareholders.value?.find((item) => item.id === id && item.type === type)
  }

  const listShareholders = createComputedList<Investor>('shareholders')

  const getInvestor = (id: string, type: InvestorType): Investor => {
    return listInvestors.value?.find((item) => item.id === id && item.type === type)
  }

  const listInvestors = createComputedList<Investor>('investors')
  const listInvestorsForInvestors = createListBy<Investor>('investors')
  const listSelectedInvestors = computed(() => listInvestorsForInvestors(selectedInvestorKeys.value))

  ///////////////////////////////////////////////////////////////////////////////
  // Invoices
  ///////////////////////////////////////////////////////////////////////////////

  const getInvoice = createGet<Invoice>('invoices')
  const listInvoices = createComputedList<Invoice>('invoices')

  ///////////////////////////////////////////////////////////////////////////////
  // Notes
  ///////////////////////////////////////////////////////////////////////////////

  const getNote = createGet<Note>('notes')
  const listNotes = createComputedList<Note>('notes')

  ///////////////////////////////////////////////////////////////////////////////
  // TermSheets
  ///////////////////////////////////////////////////////////////////////////////

  const getTermSheet = createGet<TermSheet>('term_sheets')
  const listTermSheets = createComputedList<TermSheet>('term_sheets')

  ///////////////////////////////////////////////////////////////////////////////

  return {
    // State
    entity,
    entityExpanded,
    metricSeries,
    metricSeriesLoading,

    // Actions
    fetch,
    fetchEntity,
    fetchInsights,
    fetchMetricSeries,
    fetchCalls,
    fetchCommitments,
    fetchDistributions,
    fetchInvestments,

    // Commands
    generateOnboardingTaskList,
    setupSpv,
    setupFund,
    updateTaxId,
    deleteEntity,
    closeEntity,
    exitEntity,
    fetchExitReadiness,
    fetchCloseReadiness,
    reopenEntity,
    recloseEntity,
    reset,

    // Getters

    getAdmin,
    listAdmins,

    getCall,
    listCalls,
    listCallsForInvestors,
    listCallsForSelectedInvestors,

    getCommitment,
    getCommitmentTotalsForSelectedInvestors,
    listCommitments,
    listCommitmentsForInvestors,
    listCommitmentsForSelectedInvestors,

    getCompany,
    listCompanies,

    getIndustry,
    listIndustries,

    getDistribution,
    getDistributionTotalsForSelectedInvestors,
    listDistributions,
    listDistributionsForInvestors,
    listDistributionsForSelectedInvestors,

    getInvestment,
    getInvestmentTotalsForSelectedInvestors,
    listExpandedInvestmentsForSelectedInvestors,
    listInvestments,
    listInvestmentsForInvestors,
    listInvestmentsForSelectedInvestors,

    getInvestor,
    listInvestors,

    getShareholder,
    listShareholders,

    getInvoice,
    listInvoices,

    getNote,
    listNotes,

    getTermSheet,
    listTermSheets,

    selectedInvestorKeys,
    createKeyForInvestor,
    selectAllInvestorKeys,
    documentSigningTask,
  }
})
